<template>
  <DialogPrime
    v-model:visible="localVisible"
    modal
    header="Add Item"
    :style="{ width: '480px', minWidth: '360px' }"
  >
    <div class="row form-row-spacing">
      <div v-if="showMaterialNumberField" class="col-12 mb-3">
        <label class="mb-1">Material Number</label>
        <AutoComplete :model-value="materialNumberSearchText" size="small"
          :suggestions="secondary.optMaterialNumber.data" option-label="label" option-value="value"
          placeholder="Search Material Number" :auto-option-focus="false" complete-on-focus fluid
          @update:model-value="(val) => { materialNumberSearchText = typeof val === 'string' ? val : materialNumberSearchText; }"
          @complete="searchMaterialNumber" @item-select="onSelectMaterialNumber">
          <template #option="{ option }">
            <div>
              <div style="font-size:0.85rem;font-weight:600">{{ option.label }}</div>
              <div style="font-size:0.75rem;color:#6b7280">{{ option.short_text }}</div>
            </div>
          </template>
        </AutoComplete>
      </div>
      <div class="col-12">
        <label class="mb-1">Plant</label>
        <AutoComplete :model-value="plantSearchText" size="small" :suggestions="secondary.optPlant.data"
          option-label="label" placeholder="Search Plant" :auto-option-focus="false" complete-on-focus
          :disabled="isPlantDisabled" fluid
          @update:model-value="(val) => { plantSearchText = typeof val === 'string' ? val : plantSearchText; }"
          @complete="searchPlantOptions" @item-select="onSelectPlant">
          <template #option="{ option }">
            <div class="d-flex align-items-center gap-2">
              <strong>{{ option.code }}</strong>
              <span class="text-muted">- {{ option.description }}</span>
            </div>
          </template>
        </AutoComplete>
      </div>
    </div>

    <template #footer>
      <ButtonPrime label="Cancel" severity="secondary" outlined :disabled="onLoading" @click="close" />
      <ButtonPrime label="Add Item" :disabled="!canSubmit || onLoading" @click="submit">
        <template v-if="onLoading" #icon>
          <i class="pi pi-spin pi-spinner me-2"></i>
        </template>
      </ButtonPrime>
    </template>
  </DialogPrime>
</template>

<script>
import api from "@/helpers/api";
import euMaterialService from "@/services/generalMasterData/euMaterialService";
import generalMasterDataService from "@/services/generalMasterData/generalMasterDataService";
import materialNumberFormatter from "@/helpers/materialNumberFormatter";

const getClientCode = () => {
  try { return JSON.parse(localStorage.getItem('userAuth') || '{}')?.user?.client_code || ''; } catch { return ''; }
};
const getCompanyCode = () => {
  try { return JSON.parse(localStorage.getItem('userAuth') || '{}')?.user?.company_code || ''; } catch { return ''; }
};
// District user (ANTAM, dari token SSO). Kosong -> opsi plant semua plant client.
const getDistrictCode = () => {
  try { return JSON.parse(localStorage.getItem('userAuth') || '{}')?.user?.district_code || ''; } catch { return ''; }
};

export default {
  name: "AddItemModal",
  props: {
    visible: { type: Boolean, default: false },
    // mcr_type header MCR: MCR001 = create (cuma Plant), MCR002 = change (Material Number dulu, lalu Plant milik material itu).
    // Pengganti prop formType (FORM001D/E) versi lama -- file New_* tidak memakai form_type lagi.
    mcrType: { type: String, default: "" },
    // Plant yang WAJIB dipakai item baru (plant item yang sudah ada di MCR; aturan dihitung New_CataloguerForm). null = bebas.
    requiredPlant: { type: String, default: null },
    onLoading: { type: Boolean, default: false }
  },
  emits: ["update:visible", "submit"],
  data() {
    return {
      form: {
        material_number: "",
        plant: "",
      },
      materialNumberSearchText: "",
      plantSearchText: "",
      secondary: {
        optMaterialNumber: { data: [] },
        optPlant: { data: [] }
      },
      // Plant district user (cache selama komponen hidup; district tidak berubah selama sesi login).
      districtPlants: null
    };
  },
  computed: {
    isChange() {
      return String(this.mcrType || "").toUpperCase() === "MCR002";
    },
    localVisible: {
      get() { return this.visible; },
      set(val) { this.$emit("update:visible", val); }
    },
    showMaterialNumberField() {
      return this.isChange;
    },
    isPlantDisabled() {
      return this.isChange && !this.form.material_number;
    },
    canSubmit() {
      if (!this.form.plant) return false;
      if (this.isChange && !this.form.material_number) return false;
      return true;
    }
  },
  watch: {
    visible(val) {
      if (val) this.resetForm();
    }
  },
  methods: {
    resetForm() {
      this.form = { material_number: "", plant: "" };
      this.materialNumberSearchText = "";
      this.plantSearchText = "";
      this.secondary.optMaterialNumber.data = [];
      this.secondary.optPlant.data = [];
    },
    async fetchMaterialNumberOptions(params = {}) {
      const resp = await api(this, euMaterialService.list({ company: getCompanyCode(), ...params }), false);
      const list = Array.isArray(resp.data) ? resp.data : (Array.isArray(resp.data?.data) ? resp.data.data : []);
      return list.map((i) => ({
        label: materialNumberFormatter.formatMaterialNumber(i.material_number),
        value: materialNumberFormatter.formatMaterialNumber(i.material_number),
        raw: i.material_number,
        inc: i.inc,
        short_text: i.short_text,
      }));
    },
    async searchMaterialNumber(event) {
      const query = event.query?.trim();
      if (!query || query.length < 2) {
        try { this.secondary.optMaterialNumber.data = await this.fetchMaterialNumberOptions({ limit: 7 }); }
        catch (error) { this.secondary.optMaterialNumber.data = []; }
        return;
      }
      try {
        this.secondary.optMaterialNumber.data = await this.fetchMaterialNumberOptions({ search: query });
      } catch (error) { this.secondary.optMaterialNumber.data = []; }
    },
    onSelectMaterialNumber({ value }) {
      if (!value) return;
      const selectedValue = typeof value === "object" ? (value.value ?? value) : value;
      const suggestion = this.secondary.optMaterialNumber.data.find(s => s.value === selectedValue);
      const raw = suggestion?.raw || selectedValue;
      this.materialNumberSearchText = selectedValue; // formatted (tampilan)
      this.form.material_number = raw;               // raw (untuk API/plant lookup)
      // Opsi plant tergantung material number (change / MCR002) — reset pilihan plant lama.
      this.form.plant = "";
      this.plantSearchText = "";
      this.secondary.optPlant.data = [];
    },
    // Plant di district user (GET district?company&code -> district_plants[].plant), sama dengan opsi Plant FORM02 ANTAM (config).
    async fetchDistrictPlants() {
      if (this.districtPlants) return this.districtPlants;
      const resp = await api(this, generalMasterDataService.list("district", { company: getCompanyCode(), code: getDistrictCode() }), false);
      const districts = Array.isArray(resp.data) ? resp.data : (Array.isArray(resp.data?.data) ? resp.data.data : []);
      this.districtPlants = (districts[0]?.district_plants || [])
        .map((row) => row.plant)
        .filter((plant) => plant?.code)
        .map((plant) => ({ code: plant.code, description: plant.description, label: `${plant.code} - ${plant.description}` }));
      return this.districtPlants;
    },
    async fetchPlantOptions(params = {}) {
      // Create (MCR001): user ber-district cuma melihat plant district-nya (dicari lokal, daftarnya kecil). Change (MCR002) tetap
      // plant milik material.
      if (!this.isChange && getDistrictCode()) {
        const search = String(params.search || "").toUpperCase();
        return (await this.fetchDistrictPlants()).filter((plant) => !search || plant.label.toUpperCase().includes(search));
      }
      const resource = this.isChange ? "call-plant-by-matnum" : "plant";
      const extraParams = this.isChange
        ? { material_number: this.form.material_number, ...params }
        : params;
      const resp = await api(this, generalMasterDataService.list(resource, { client: getClientCode(), ...extraParams }), false);
      const list = Array.isArray(resp.data) ? resp.data : (Array.isArray(resp.data?.data) ? resp.data.data : []);
      return list.map((item) => ({ code: item.code, description: item.description, label: `${item.code} - ${item.description}` }));
    },
    async searchPlantOptions(event) {
      const query = event.query?.trim();
      if (!query || query.length < 2) {
        try { this.secondary.optPlant.data = await this.fetchPlantOptions({ limit: 7 }); }
        catch (error) { this.secondary.optPlant.data = []; }
        return;
      }
      try {
        this.secondary.optPlant.data = await this.fetchPlantOptions({ search: query.toUpperCase() });
      } catch (error) { this.secondary.optPlant.data = []; }
    },
    onSelectPlant({ value }) {
      if (!value) return;
      if (this.requiredPlant && value.code !== this.requiredPlant) {
        this.form.plant = "";
        this.plantSearchText = "";
        this.$swal({
          icon: "warning",
          title: "Warning",
          text: `All items in this MCR must use the same plant. Please select plant ${this.requiredPlant}.`,
          confirmButtonText: "Close",
        });
        return;
      }
      this.plantSearchText = value.label;
      this.form.plant = value.code;
    },
    submit() {
      if (!this.canSubmit) return;
      if (this.requiredPlant && this.form.plant !== this.requiredPlant) return;
      this.$emit("submit", { ...this.form });
    },
    close() {
      this.$emit("update:visible", false);
    }
  }
};
</script>
